import * as React from 'react'

import { CirclePlusIcon, Trash2Icon, XIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { IUserProject } from '~/client/graph'
import TagsSelector from '~/client/src/desktop/components/TagsSelector/TagsSelector'
import { Content, Footer, Header } from '~/client/src/shared/components/Layout'
import { Loader } from '~/client/src/shared/components/Loader'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { SidePanel } from '~/client/src/shared/components/SidePanel/SidePanel'
import { TagType } from '~/client/src/shared/enums/TagType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import User from '~/client/src/shared/models/User'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'

import BulkEditMembersSideBarStore from './BulkEditMembersSideBar.store'

import Colors from '~/client/src/shared/theme.module.scss'

interface IProps {
  selectedProjectMembers: User[]
  onClose: () => void
  bulkEditTags: (items: IUserProject[]) => void
  loading: boolean
  errorMessage?: string

  toggleInviteDialog: (isShow: boolean) => void
  toggleDeleteDialog: (isShow: boolean) => void
  hasProjectMembersToInvite: boolean
  tagsStore?: TagsStore
  userProjectsStore?: UserProjectsStore
}

const availableTypes = [
  TagType.Team,
  TagType.Role,
  TagType.AccountType,
  TagType.Company,
]

@inject('tagsStore', 'userProjectsStore')
@observer
export default class BulkEditMembersSideBar extends React.Component<IProps> {
  private readonly store: BulkEditMembersSideBarStore = null

  public constructor(props: IProps) {
    super(props)

    this.store = new BulkEditMembersSideBarStore(
      props.tagsStore,
      props.selectedProjectMembers,
      props.bulkEditTags,
      availableTypes,
      props.userProjectsStore,
    )
  }

  public componentDidMount() {
    const { onClose, selectedProjectMembers } = this.props

    if (!selectedProjectMembers.length) {
      onClose()
    }
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    const { onClose, selectedProjectMembers } = this.props

    if (!selectedProjectMembers.length) {
      onClose()
    }

    if (selectedProjectMembers !== prevProps.selectedProjectMembers) {
      this.store.init(selectedProjectMembers)
    }
  }

  public render() {
    const content = this.props.loading
      ? this.renderLoader()
      : this.renderContent()

    return <SidePanel>{content}</SidePanel>
  }

  private renderContent(): JSX.Element {
    const { onClose, errorMessage } = this.props
    const { changeSelection, defaultSelection, handleApply, canApply } =
      this.store

    return (
      <>
        <Header className="tw:flex tw:flex-col tw:p-2.5 tw:px-5 tw:border-0 tw:border-b tw:border-solid tw:border-border tw:grow-0">
          <div className="tw:flex tw:items-center">
            <XIcon
              size={16}
              className="tw:grow-0 tw:cursor-pointer tw:mr-3"
              color={Colors.neutral60}
              onClick={onClose}
            />
            <span className="tw:flex-1 tw:text-[17px] tw:font-semibold tw:text-foreground">
              {Localization.translator.edit_verb}
            </span>
            <Button
              className="bold"
              variant="default"
              disabled={!canApply}
              onClick={handleApply}
            >
              {Localization.translator.apply}
            </Button>
          </div>
          <div className="tw:text-body-md tw:text-primary tw:py-[5px]">
            {this.selectedProjectMembersLabel}
          </div>
        </Header>
        <Content className="tw:px-5">
          <TagsSelector
            onChange={changeSelection}
            availableTypes={availableTypes}
            initSelection={defaultSelection}
            radioTagTypes={[TagType.Company, TagType.AccountType]}
            separatedCategories={[TagType.Company]}
          />
          {this.renderActionButons()}
        </Content>
        <Footer>
          <div className="tw:text-title-sm tw:text-center tw:text-destructive tw:pb-6">
            {errorMessage}
          </div>
        </Footer>
      </>
    )
  }

  private renderActionButons() {
    const {
      toggleDeleteDialog,
      toggleInviteDialog,
      hasProjectMembersToInvite,
    } = this.props
    return (
      <div className="tw:flex tw:flex-col">
        <div className="tw:border-0 tw:border-t tw:border-solid tw:border-border tw:mb-2.5" />
        <Button
          key="delete"
          className="tw:min-w-[90px] tw:max-w-[90px] tw:mb-2.5"
          variant="primary-outline"
          onClick={() => toggleDeleteDialog(true)}
        >
          <Trash2Icon data-icon="inline-start" />
          {Localization.translator.delete}
        </Button>
        <Button
          key="invite"
          className="tw:min-w-[90px] tw:max-w-[90px]"
          variant="primary-outline"
          disabled={!hasProjectMembersToInvite}
          onClick={() => toggleInviteDialog(true)}
        >
          <CirclePlusIcon data-icon="inline-start" />
          {Localization.translator.invite}
        </Button>
      </div>
    )
  }

  private renderLoader(): JSX.Element {
    return (
      <div className="tw:h-full tw:w-full tw:absolute tw:z-[99] tw:bg-black/40">
        <Loader />
      </div>
    )
  }

  private get selectedProjectMembersLabel(): string {
    return Localization.translator.xTeamMembersSelected(
      this.props.selectedProjectMembers.length,
    )
  }
}
